import { OTRateType, OTRateUnit } from 'api/gql-utils/overtime/types';
import { routePaths } from 'components/Routes/data';
import type {
  AttendanceSettingsFormState,
  AttendanceSettingsTabs,
  OvertimeFormulaFormData,
} from 'components/Settings/AttendanceAndLeavesSettings/types';
import { ValidationSchema } from 'forms/types';
import { INTEGER_AMOUNT, NUMBER_REGEX } from 'utils/regexes';

// Copy used in the leave-policies card.
export const LEAVE_POLICY_COPY = {
  allowHalfdays: {
    title: 'Allow half-day leaves',
    description: 'Employees can apply for half-day leave instead of a full day.',
  },
  compulsoryLeaveRemarks: {
    title: 'Require a remark',
    description: 'Employees must add a reason when applying for any leave or half-day.',
  },
  allowNegativeLeaves: {
    title: 'Allow negative leave balance',
    description:
      'Employees can request leave with a zero balance; the deficit is recovered from the next cycle.',
  },
  autoAttendanceLop: {
    title: 'Auto-deduct loss-of-pay for unpaid leaves',
    description:
      'Unpaid leaves automatically reduce salary. Calculated using your base-days setting.',
  },
} as const;

// Value-prop strings shown on the disabled hero.
export const ENABLE_VALUE_PROPS: { title: string; description: string }[] = [
  {
    title: 'Overtime & leave calculations',
    description: 'OT hours auto-calculated from shift rules.',
  },
  {
    title: 'Attendance-to-payslip sync',
    description: 'Attendance flows straight into the payslip.',
  },
  {
    title: 'Compliance-ready audit trail',
    description: 'OT caps and Form 25 generated automatically.',
  },
];

// Saturday chip labels for the WeeklyOff component.
export const SATURDAY_CHIPS: {
  field: keyof AttendanceSettingsFormState;
  label: string;
}[] = [
  { field: 'weekendSat1', label: '1st Sat' },
  { field: 'weekendSat2', label: '2nd Sat' },
  { field: 'weekendSat3', label: '3rd Sat' },
  { field: 'weekendSat4', label: '4th Sat' },
  { field: 'weekendSat5', label: '5th Sat' },
];

export const TAB_ROUTES: Record<AttendanceSettingsTabs, string> = {
  attendance: routePaths.orgSettings.timeAndAttendance.attendance,
  leaves: routePaths.orgSettings.timeAndAttendance.leaves,
  'pay-rules': routePaths.orgSettings.timeAndAttendance.payRules,
};

export const FIELD_TO_TAB: Record<string, AttendanceSettingsTabs> = {
  shiftStart: 'attendance',
  shiftEnd: 'attendance',
  leaveTypes: 'leaves',
};

export const FORMULA_RATE_UNIT: Record<OTRateUnit, OTRateUnit> = {
  per_hour: 'per_hour',
  per_day: 'per_day',
};

export const FORMULA_RATE_TYPE: Record<OTRateType, OTRateType> = {
  flat: 'flat',
  multiplier: 'multiplier',
  flat_plus_multiplier: 'flat_plus_multiplier',
};

export const FORMULA_UNIT_DISPLAY: Record<OTRateUnit, string> = {
  per_hour: 'hour',
  per_day: 'day',
};

export const MULTIPLIER_LABELS: Record<string, string> = {
  '1.5': 'Time and Half',
  '2': 'Double Time',
  '3': 'Triple Time',
};

export const SELECT_ALL_VALUE = '__select_all__';

export const OVERTIME_TYPE = {
  weeklyOff: 1,
  publicHoliday: 2,
  workingDay: 3,
  nightTime: 4,
} as const;

export type OvertimeTypeValue = typeof OVERTIME_TYPE[keyof typeof OVERTIME_TYPE];

export const OVERTIME_TYPE_LABELS: Record<OvertimeTypeValue, string> = {
  [OVERTIME_TYPE.weeklyOff]: 'Weekly Off',
  [OVERTIME_TYPE.publicHoliday]: 'Public Holiday',
  [OVERTIME_TYPE.workingDay]: 'Working Day',
  [OVERTIME_TYPE.nightTime]: 'Night Time',
};

export const OVERTIME_TYPE_API_VALUE: Record<OvertimeTypeValue, string> = {
  [OVERTIME_TYPE.weeklyOff]: 'weekly_off',
  [OVERTIME_TYPE.publicHoliday]: 'public_holiday',
  [OVERTIME_TYPE.workingDay]: 'working_day',
  [OVERTIME_TYPE.nightTime]: 'night_time',
};

export const OVERTIME_TYPE_FROM_API_VALUE: Record<string, OvertimeTypeValue> = {
  weekly_off: OVERTIME_TYPE.weeklyOff,
  public_holiday: OVERTIME_TYPE.publicHoliday,
  working_day: OVERTIME_TYPE.workingDay,
  night_time: OVERTIME_TYPE.nightTime,
};

export const OVERTIME_TYPE_CARDS: {
  type: OvertimeTypeValue;
  label: string;
  description: string;
}[] = [
  {
    type: OVERTIME_TYPE.workingDay,
    label: OVERTIME_TYPE_LABELS[OVERTIME_TYPE.workingDay],
    description: 'Overtime paid for extra hours worked on a regular working day.',
  },
  {
    type: OVERTIME_TYPE.nightTime,
    label: OVERTIME_TYPE_LABELS[OVERTIME_TYPE.nightTime],
    description: 'Overtime paid for hours worked during night shifts.',
  },
  {
    type: OVERTIME_TYPE.weeklyOff,
    label: OVERTIME_TYPE_LABELS[OVERTIME_TYPE.weeklyOff],
    description: 'Overtime paid for hours worked on the weekly off day.',
  },
  {
    type: OVERTIME_TYPE.publicHoliday,
    label: OVERTIME_TYPE_LABELS[OVERTIME_TYPE.publicHoliday],
    description: 'Overtime paid for hours worked on a public holiday.',
  },
];

export const INITIAL_FORM_STATE: OvertimeFormulaFormData = {
  rateType: undefined,
  multiplier: undefined,
  customMultiplierValue: undefined,
  flatAmount: undefined,
  unit: undefined,
  dailyAllowanceAmount: undefined,
  formulaName: '',
  selectedComponentIds: [],
  showDailyAllowance: false,
};

export const numberRegex = new RegExp(NUMBER_REGEX);

export const validationSchema: ValidationSchema<OvertimeFormulaFormData> = {
  rateType: (data) => (!data.rateType ? 'Please select a rate type' : null),
  unit: (data) => (!data.unit ? 'Please select a pay unit' : null),
  formulaName: (data) => {
    if (!data.formulaName?.trim()) return 'Formula name is required';
    if (data.formulaName.length > 250) return 'Formula name must be less than 250 characters';
    return null;
  },
  flatAmount: (data) => {
    if (data.rateType !== FORMULA_RATE_TYPE.flat) return null;
    if (!data.flatAmount) return 'Amount is required';
    if (!INTEGER_AMOUNT.test(data.flatAmount)) return 'Please enter a valid amount';
    if (Number(data.flatAmount) <= 0) return 'Amount must be greater than 0';
    return null;
  },
  multiplier: (data) =>
    data.rateType === FORMULA_RATE_TYPE.multiplier && !data.multiplier
      ? 'Please select a multiplier'
      : null,
  customMultiplierValue: (data) => {
    if (data.rateType !== FORMULA_RATE_TYPE.multiplier || data.multiplier !== 'custom') return null;
    if (!data.customMultiplierValue) return 'Please enter a custom multiplier value';
    if (!numberRegex.test(data.customMultiplierValue)) return 'Please enter a valid number';
    if (Number(data.customMultiplierValue) <= 0) return 'Multiplier must be greater than 0';
    return null;
  },
  dailyAllowanceAmount: (data) => {
    if (!data.showDailyAllowance) return null;
    if (!data.dailyAllowanceAmount || !INTEGER_AMOUNT.test(data.dailyAllowanceAmount))
      return 'Please enter a valid amount';
    if (Number(data.dailyAllowanceAmount) <= 0) return 'Amount must be greater than 0';
    return null;
  },
  selectedComponentIds: (data) =>
    data.rateType === FORMULA_RATE_TYPE.multiplier && !data.selectedComponentIds?.length
      ? 'Please select at least one component'
      : null,
};
